/**
 * Microsoft Graph Service
 *
 * Service pour gérer l'intégration SharePoint via Microsoft Graph SDK
 * Utilise le MSAL2Provider existant pour éviter les conflits d'authentification
 */

import { Client } from '@microsoft/microsoft-graph-client';
import { AuthenticationProvider } from '@microsoft/microsoft-graph-client';
import { Providers } from '@microsoft/mgt-element';
import { DriveItem, Site, Drive } from '@microsoft/microsoft-graph-types';
import { SharePointFileInfo } from '@/types/api';

/**
 * Custom Authentication Provider qui utilise MSAL2Provider existant
 * Évite les conflits d'authentification avec OneDrive Picker
 */
class MSALGraphAuthProvider implements AuthenticationProvider {
  async getAccessToken(): Promise<string> {
    if (!Providers.globalProvider) {
      throw new Error('MSAL2Provider not initialized');
    }

    try {
      // Utilise le provider existant pour obtenir le token
      const accessToken = await Providers.globalProvider.getAccessToken({
        scopes: ['Files.Read.All', 'Sites.Read.All']
      });

      if (!accessToken) {
        throw new Error('Failed to acquire access token');
      }

      return accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw new Error('Authentication failed: Unable to get access token');
    }
  }
}

export class MicrosoftGraphService {
  private graphClient: Client;
  private authProvider: MSALGraphAuthProvider;

  constructor() {
    this.authProvider = new MSALGraphAuthProvider();

    // Initialisation du client Graph avec provider custom
    this.graphClient = Client.initWithMiddleware({
      authProvider: this.authProvider
    });
  }

  /**
   * Récupère la liste des sites SharePoint accessibles
   * Utilise uniquement les permissions déléguées minimales pour garantir
   * l'accès limité au contexte de l'utilisateur connecté
   * @returns Promise<Site[]>
   */
  async getSites(): Promise<Site[]> {
    console.log('🔍 [Graph API] Starting secure site discovery (delegated permissions only)...');
    const allSites: Site[] = [];
    const siteIds = new Set<string>(); // Pour éviter les doublons

    try {
      // Méthode 1: Sites de base (root sites)
      console.log('🔍 [Graph API] Method 1: Calling /sites...');
      try {
        const response = await this.graphClient.api('/sites').get();
        console.log('🔍 [Graph API] Basic sites response:', response);
        if (response.value) {
          response.value.forEach((site: Site) => {
            if (site.id && !siteIds.has(site.id)) {
              allSites.push(site);
              siteIds.add(site.id);
            }
          });
        }
      } catch (basicError) {
        console.log('🔍 [Graph API] Basic sites method failed:', basicError);
      }

      // Méthode 2: Recherche de tous les sites accessibles
      console.log('🔍 [Graph API] Method 2: Searching accessible sites...');
      try {
        const searchResponse = await this.graphClient
          .api('/sites')
          .query({ search: '*' })
          .get();
        console.log('🔍 [Graph API] Search sites response:', searchResponse);
        if (searchResponse.value) {
          searchResponse.value.forEach((site: Site) => {
            if (site.id && !siteIds.has(site.id)) {
              allSites.push(site);
              siteIds.add(site.id);
            }
          });
        }
      } catch (searchError) {
        console.log('🔍 [Graph API] Search method failed:', searchError);
      }

      // Méthode 3: Sites suivis par l'utilisateur (si disponible)
      console.log('🔍 [Graph API] Method 3: Getting followed sites...');
      try {
        const followedResponse = await this.graphClient.api('/me/followedSites').get();
        console.log('🔍 [Graph API] Followed sites response:', followedResponse);
        if (followedResponse.value) {
          followedResponse.value.forEach((site: Site) => {
            if (site.id && !siteIds.has(site.id)) {
              allSites.push(site);
              siteIds.add(site.id);
            }
          });
        }
      } catch (followedError) {
        console.log('🔍 [Graph API] Followed sites method failed (may require additional permissions):', followedError);
      }

      console.log(`🔍 [Graph API] Total unique sites discovered: ${allSites.length}`);
      console.log('🔍 [Graph API] All discovered sites:', allSites);
      console.log('🔍 [Graph API] Security note: Access limited to user context via delegated permissions');

      return allSites;
    } catch (error) {
      console.error('🔍 [Graph API] Error getting SharePoint sites:', error);
      throw new Error('Failed to load SharePoint sites');
    }
  }

  /**
   * Récupère les éléments d'un drive (fichiers et dossiers)
   * @param siteId ID du site SharePoint (optionnel, utilise OneDrive si non fourni)
   * @param folderId ID du dossier (optionnel, utilise racine si non fourni)
   * @returns Promise<DriveItem[]>
   */
  async getDriveItems(siteId?: string, folderId?: string): Promise<DriveItem[]> {
    try {
      let endpoint: string;

      if (siteId) {
        // SharePoint site drive
        if (folderId) {
          endpoint = `/sites/${siteId}/drive/items/${folderId}/children`;
        } else {
          endpoint = `/sites/${siteId}/drive/root/children`;
        }
      } else {
        // OneDrive personnel
        if (folderId) {
          endpoint = `/me/drive/items/${folderId}/children`;
        } else {
          endpoint = `/me/drive/root/children`;
        }
      }

      console.log('🔍 [Graph API] Calling endpoint:', endpoint);

      const response = await this.graphClient
        .api(endpoint)
        .get();

      console.log('🔍 [Graph API] Drive items response:', response);
      return response.value || [];
    } catch (error) {
      console.error('🔍 [Graph API] Error getting drive items:', error);
      throw new Error('Failed to load files and folders');
    }
  }

  /**
   * Recherche des fichiers par nom
   * @param query Requête de recherche
   * @param siteId ID du site SharePoint (optionnel)
   * @returns Promise<DriveItem[]>
   */
  async searchFiles(query: string, siteId?: string): Promise<DriveItem[]> {
    try {
      if (!query.trim()) {
        return [];
      }

      let endpoint: string;
      if (siteId) {
        endpoint = `/sites/${siteId}/drive/search(q='${encodeURIComponent(query)}')`;
      } else {
        endpoint = `/me/drive/search(q='${encodeURIComponent(query)}')`;
      }

      const response = await this.graphClient
        .api(endpoint)
        .get();

      return response.value || [];
    } catch (error) {
      console.error('Error searching files:', error);
      throw new Error('Failed to search files');
    }
  }

  /**
   * Transforme un DriveItem en SharePointFileInfo pour compatibilité backend
   * @param item DriveItem de Graph API
   * @returns SharePointFileInfo
   */
  transformToSharePointFileInfo(item: DriveItem): SharePointFileInfo {
    // Handle shared items where data may be in remoteItem
    const remote = (item as any).remoteItem;
    const effectiveItem = remote || item;
    const isFolder = !!(effectiveItem.folder || item.folder);

    return {
      id: effectiveItem.id || item.id || '',
      name: effectiveItem.name || item.name || 'Unknown',
      size: effectiveItem.size || item.size || 0,
      driveId: effectiveItem.parentReference?.driveId || item.parentReference?.driveId,
      path: effectiveItem.parentReference?.path || item.parentReference?.path,
      is_folder: isFolder,
      web_url: effectiveItem.webUrl || item.webUrl || undefined,
      created_at: effectiveItem.createdDateTime || item.createdDateTime || undefined,
      modified_at: effectiveItem.lastModifiedDateTime || item.lastModifiedDateTime || undefined,
    };
  }

  /**
   * Transforme un tableau de DriveItems en SharePointFileInfos
   * @param items DriveItem[] de Graph API
   * @returns SharePointFileInfo[]
   */
  transformMultipleToSharePointFileInfo(items: DriveItem[]): SharePointFileInfo[] {
    return items
      .filter(item => item.id && item.name) // Filtrer les items valides
      .map(item => this.transformToSharePointFileInfo(item));
  }

  /**
   * Vérifie si le service est authentifié et fonctionnel
   * @returns Promise<boolean>
   */
  async testConnection(): Promise<boolean> {
    try {
      console.log('🔍 [Graph API] Testing connection to /me...');

      // Test simple: récupérer le profil utilisateur
      const profile = await this.graphClient
        .api('/me')
        .get();

      console.log('🔍 [Graph API] Connection test successful for user:', profile.displayName);
      return true;
    } catch (error) {
      console.error('🔍 [Graph API] Connection test failed:', error);
      return false;
    }
  }

  /**
   * Résout un site SharePoint spécifique par hostname et chemin.
   * Utilise: GET /sites/{hostname}:{serverRelativePath}
   */
  async getConfiguredSite(hostname: string, sitePath: string): Promise<Site> {
    const endpoint = `/sites/${hostname}:${sitePath}`;
    console.log('[Graph API] Resolving configured site:', endpoint);
    const site = await this.graphClient.api(endpoint).get();
    return site;
  }

  /**
   * Liste tous les drives (bibliothèques de documents) d'un site SharePoint.
   * Utilise: GET /sites/{siteId}/drives
   */
  async getSiteDrives(siteId: string): Promise<Drive[]> {
    try {
      console.log('[Graph API] Getting drives for site:', siteId);
      const response = await this.graphClient
        .api(`/sites/${siteId}/drives`)
        .get();
      return response.value || [];
    } catch (error) {
      console.error('[Graph API] Error getting site drives:', error);
      throw new Error('Failed to load document libraries');
    }
  }

  /**
   * Récupère les éléments d'un drive spécifique (par driveId).
   * Root:   GET /drives/{driveId}/root/children
   * Folder: GET /drives/{driveId}/items/{folderId}/children
   */
  async getDriveItemsByDriveId(driveId: string, folderId?: string): Promise<DriveItem[]> {
    try {
      const endpoint = folderId
        ? `/drives/${driveId}/items/${folderId}/children`
        : `/drives/${driveId}/root/children`;
      console.log('[Graph API] Fetching drive items:', endpoint);
      const response = await this.graphClient.api(endpoint).get();
      return response.value || [];
    } catch (error) {
      console.error('[Graph API] Error getting drive items by driveId:', error);
      throw new Error('Failed to load files from library');
    }
  }

  /**
   * Récupère les fichiers partagés avec l'utilisateur.
   * Utilise: GET /me/drive/sharedWithMe
   */
  async getSharedWithMe(): Promise<DriveItem[]> {
    try {
      console.log('[Graph API] Getting shared files...');
      const response = await this.graphClient
        .api('/me/drive/sharedWithMe')
        .get();
      return response.value || [];
    } catch (error) {
      console.error('[Graph API] Error getting shared files:', error);
      throw new Error('Failed to load shared files');
    }
  }

  /**
   * Recherche des fichiers dans un drive spécifique.
   * Utilise: GET /drives/{driveId}/search(q='{query}')
   */
  async searchInDrive(query: string, driveId: string): Promise<DriveItem[]> {
    try {
      if (!query.trim()) return [];
      const endpoint = `/drives/${driveId}/search(q='${encodeURIComponent(query)}')`;
      const response = await this.graphClient.api(endpoint).get();
      return response.value || [];
    } catch (error) {
      console.error('[Graph API] Error searching in drive:', error);
      throw new Error('Failed to search files');
    }
  }

  /**
   * Récupère les informations d'un fichier spécifique
   * @param fileId ID du fichier
   * @param siteId ID du site SharePoint (optionnel)
   * @returns Promise<DriveItem>
   */
  async getFileDetails(fileId: string, siteId?: string): Promise<DriveItem> {
    try {
      let endpoint: string;
      if (siteId) {
        endpoint = `/sites/${siteId}/drive/items/${fileId}`;
      } else {
        endpoint = `/me/drive/items/${fileId}`;
      }

      const item = await this.graphClient
        .api(endpoint)
        .get();

      return item;
    } catch (error) {
      console.error('Error getting file details:', error);
      throw new Error('Failed to get file details');
    }
  }
}

// Export d'une instance singleton
export const microsoftGraphService = new MicrosoftGraphService();

// Export de la classe pour les tests
export default MicrosoftGraphService;